import { useEffect } from "react";
import { isRouteErrorResponse, useNavigate, useParams, useRouteError, useSearchParams } from "react-router";
import { Warning } from "@phosphor-icons/react";
import { IErrorResponse } from "../../utils";
import { useLocationWrapper } from "../../hooks/useLocationWrapper";
import { analytics } from "../../analytics";

import Button from "../ButtonComponent";

interface IState {
  error: IErrorResponse;
  restartRequired?: boolean;
}

const SYSTEM_ERROR_CODES = new Set([
  "connection_failed",
  "oauth_state_mismatch",
  "server_error",
  "temporarily_unavailable",
  "unknown_error",
  "UNKNOWN_ERROR",
  "INTERNAL_SERVER_ERROR",
  "UNAUTHORIZED",
]);

interface IErrorConfig {
  message: string;
  showTryAgain: boolean;
}

function getTextureErrorConfig(textureError: string): IErrorConfig {
  switch (textureError) {
    case "access_denied":
      return { message: "Looks like you cancelled. Want to try again?", showTryAgain: true };
    case "device_incompatible":
      return { message: "Your device isn't compatible with this program.", showTryAgain: true };
    case "subscription_required":
      return { message: "A subscription is required to connect your account.", showTryAgain: true };
    case "connection_failed":
    case "server_error":
      return { message: "We couldn't connect to your account. Please try again.", showTryAgain: true };
    case "temporarily_unavailable":
      return { message: "The service is temporarily unavailable. Please try again later.", showTryAgain: true };
    case "connect_session_expired":
      return {
        message: "Your session has expired. Please reach out to your provider for a new connection link.",
        showTryAgain: false,
      };
    case "connect_session_already_completed":
      return { message: "This connection has already been completed.", showTryAgain: false };
    case "oauth_state_mismatch":
      return {
        message: "There was a security error. Please reach out to your provider for a new connection link.",
        showTryAgain: false,
      };
    default:
      return { message: "Something went wrong. Please try again.", showTryAgain: true };
  }
}

function getStateErrorConfig(error: IErrorResponse): IErrorConfig {
  switch (error.code) {
    case "ALREADY_ENROLLED_IN_ANOTHER_PROGRAM":
      return {
        message:
          "Your system is already enrolled in another program, so it can't be enrolled in this one. Please reach out to your provider for help.",
        showTryAgain: false,
      };
    case "AUTHORIZATION_CODE_NOT_FOUND":
      return { message: "We couldn't finish connecting your account. Please try again.", showTryAgain: true };
    case "UNAUTHORIZED":
      return { message: error.message || "An error occurred", showTryAgain: false };
    case "INTERNAL_SERVER_ERROR":
    case "UNKNOWN_ERROR":
      return { message: "Something went wrong. Please try again.", showTryAgain: true };
    default:
      return { message: error.message || "An error occurred", showTryAgain: true };
  }
}

export function ConnectError() {
  const { state } = useLocationWrapper<IState>();
  const [searchParams] = useSearchParams();
  const { linkToken } = useParams();
  const navigate = useNavigate();

  const stateError: IErrorResponse = state?.error;
  const routeError = useRouteError();
  const textureError = searchParams.get("texture_error");
  const legacySearchError = searchParams.get("error");

  let message = "An error occurred";
  let showTryAgain = true;
  let shouldRestart = false;

  if (isRouteErrorResponse(routeError)) {
    message = routeError.data || message;
    showTryAgain = false;
  } else if (stateError) {
    const config = getStateErrorConfig(stateError);
    message = config.message;
    showTryAgain = config.showTryAgain;
    // An OAuth authorization code is single-use, so navigate(-1) back to the oauth:code page
    // would only replay it. Restarting re-runs OAuth and gets a fresh code.
    shouldRestart = !!state?.restartRequired || stateError.code === "AUTHORIZATION_CODE_NOT_FOUND";
  } else if (textureError) {
    const config = getTextureErrorConfig(textureError);
    message = config.message;
    showTryAgain = config.showTryAgain;
    // OAuth redirects break browser history (user was sent to an external page),
    // so navigate(-1) would land on the manufacturer's site. Restart from the
    // beginning of the connect flow for any retryable texture_error.
    if (showTryAgain) {
      shouldRestart = true;
    }
  } else if (legacySearchError) {
    if (legacySearchError.startsWith("oauth_")) {
      message = "We were unable to connect to your account";
    }
  } else if (linkToken) {
    // Likely the user deep linked into a route
    // lets just give them the option to restart the flow
    shouldRestart = true;
  }

  const errorCode = textureError ?? stateError?.code ?? "unknown";

  useEffect(() => {
    if (analytics && analytics.track) {
      analytics.track("connect error viewed", {
        code: errorCode,
        isSystemError: SYSTEM_ERROR_CODES.has(errorCode),
      });
    }
  }, [errorCode]);

  const handleTryAgain = () => {
    if (shouldRestart) {
      navigate(`/${linkToken}`);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="w-full flex flex-col items-center gap-8 tp:gap-4">
      <Warning weight="fill" className="w-32 h-32 text-texturePennBlue" />
      <div className="text-texturePennBlue font-bold text-xl d:text-4xl tp:text-2xl text-center flex flex-row items-center gap-4">
        <span>{message}</span>
      </div>
      {showTryAgain && (
        <div className="w-full sm:w-[30rem]">
          <Button onClick={handleTryAgain}>Try again</Button>
        </div>
      )}
    </div>
  );
}
